import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";

/**
 * Separate admin login screen — dark editorial styling, minimal chrome,
 * strictly gated (rejects non-admin accounts).
 */
export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { refresh, logout } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      // Direct API call so we can inspect role before context update.
      const { data } = await api.post("/auth/login", { email, password });
      if (data.role !== "admin") {
        await logout();
        setErr("This account does not have admin access.");
        setLoading(false);
        return;
      }
      await refresh();
      navigate("/admin");
    } catch (e) {
      setErr(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--navy-deep)] text-white grain relative flex flex-col" data-testid="admin-login-page">
      <SEO title="Admin Login" description="Catalyst BH Consulting admin access" path="/admin/login" />

      {/* Gold top bar */}
      <div className="h-[6px] bg-[var(--gold)]" />

      {/* Header */}
      <header className="flex items-center justify-between px-[6vw] py-8">
        <Link to="/" className="flex items-center gap-3" data-testid="admin-brand-home">
          <div className="w-10 h-10 border border-[var(--gold)] grid place-items-center text-[var(--gold)] font-display text-xl">C</div>
          <div>
            <strong className="block tracking-[0.16em] text-[11px]">CATALYST BH CONSULTING</strong>
            <small className="block text-[9px] tracking-widest text-neutral-400 mt-[2px]">Admin console</small>
          </div>
        </Link>
        <Link to="/" className="text-neutral-400 text-[11px] tracking-widest hover:text-[var(--gold)] transition-colors">
          ← BACK TO SITE
        </Link>
      </header>

      {/* Card */}
      <main className="flex-1 grid place-items-center px-6 py-10">
        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 border border-[var(--line-dark)] px-4 py-2 mb-6">
              <Lock size={12} className="text-[var(--gold)]" />
              <span className="text-[10px] tracking-widest text-[var(--gold)] font-bold">RESTRICTED ACCESS</span>
            </div>
            <h1 className="font-display text-4xl">
              Admin <em className="italic text-[var(--gold)]">console.</em>
            </h1>
            <p className="text-neutral-400 text-sm mt-3 leading-relaxed">
              This is a private area for Catalyst operators. Member accounts should sign in{" "}
              <Link to="/login" className="text-[var(--gold)] font-bold" data-testid="member-login-link">here</Link>.
            </p>
          </div>

          <form onSubmit={submit} className="bg-[var(--navy-2)] border border-[var(--line-dark)] p-8 space-y-5" data-testid="admin-login-form">
            <div className="field">
              <label className="field-label !text-neutral-300">Admin email</label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="!bg-[var(--navy-deep)] !border-[var(--line-dark)] !text-white"
                autoComplete="username"
                data-testid="admin-login-email"
              />
            </div>
            <div className="field">
              <label className="field-label !text-neutral-300">Password</label>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="!bg-[var(--navy-deep)] !border-[var(--line-dark)] !text-white"
                autoComplete="current-password"
                data-testid="admin-login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-gold w-full justify-center"
              data-testid="admin-login-submit"
            >
              {loading ? <><Loader2 className="animate-spin mr-2" size={14} /> AUTHENTICATING…</> : (<><ShieldCheck size={14} className="mr-2" /> SIGN IN TO ADMIN</>)}
            </button>
            {err && <p className="text-red-400 text-xs" data-testid="admin-login-error">{err}</p>}
          </form>

          <p className="text-center text-[10px] tracking-widest text-neutral-500 mt-6">
            ACCESS ATTEMPTS ARE LOGGED
          </p>
        </div>
      </main>

      <footer className="px-[6vw] py-6 text-[10px] tracking-widest text-neutral-500 border-t border-[var(--line-dark)]">
        © {new Date().getFullYear()} CATALYST BH CONSULTING · ADMIN
      </footer>
    </div>
  );
}
